import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PaginatedResponse } from '@/shared/models/paginated-response.model';
import {
    AddFeedbackNotePayload,
    AdminFeedbackListQuery,
    AssignableUser,
    AssignFeedbackPayload,
    CreateFeedbackPayload,
    FeedbackAttachment,
    FeedbackDetail,
    FeedbackListItem,
    FeedbackNote,
    MyFeedbackListQuery,
    UpdateFeedbackStatusPayload
} from './feedback.model';

/**
 * Raw response shapes exactly as the backend's `FeedbackListItemResponse`/`FeedbackDetailResponse`/
 * `FeedbackNoteResponse` (see `BettaAlpha.Contracts/Models/Feedback/`) serialize — camelCased by
 * ASP.NET's default JSON naming policy. Field names deliberately diverge from this app's internal
 * `FeedbackListItem`/`FeedbackDetail` models (e.g. `createdByName` vs `createdByUserName`, and a flat
 * `contextRoute`/`contextPageTitle`/... instead of a nested `context` object), so every response is
 * mapped through `mapListItem`/`mapDetail` below rather than trusting the wire shape directly.
 */
interface RawFeedbackListItem {
    id: number;
    category: FeedbackListItem['category'];
    description: string;
    status: FeedbackListItem['status'];
    createdByRole: FeedbackListItem['createdByRole'];
    createdByUserId: number;
    createdByName: string | null;
    attachmentCount: number;
    assignedToUserId: number | null;
    createdAt: string;
}

interface RawFeedbackNote {
    id: number;
    authorUserId: number;
    authorName: string | null;
    note: string;
    createdAt: string;
}

interface RawFeedbackDetail extends RawFeedbackListItem {
    resolutionNote: string | null;
    contextRoute: string | null;
    contextPageTitle: string | null;
    contextEntityType: string | null;
    contextEntityId: number | null;
    userAgent: string | null;
    assignedToName: string | null;
    assignedByUserId: number | null;
    assignedAt: string | null;
    statusChangedAt: string | null;
    statusChangedByUserId: number | null;
    notes: RawFeedbackNote[];
    attachments: FeedbackAttachment[];
}

function mapListItem(raw: RawFeedbackListItem): FeedbackListItem {
    return {
        id: raw.id,
        category: raw.category,
        description: raw.description,
        status: raw.status,
        // The list endpoints don't return a resolution note — only the detail endpoints do.
        resolutionNote: null,
        createdByUserId: raw.createdByUserId,
        createdByUserName: raw.createdByName ?? '',
        createdByRole: raw.createdByRole,
        createdAt: raw.createdAt
    };
}

function mapNote(raw: RawFeedbackNote): FeedbackNote {
    return {
        id: raw.id,
        authorUserId: raw.authorUserId,
        authorUserName: raw.authorName,
        note: raw.note,
        createdAt: raw.createdAt
    };
}

function mapDetail(raw: RawFeedbackDetail): FeedbackDetail {
    return {
        ...mapListItem(raw),
        resolutionNote: raw.resolutionNote,
        context: {
            route: raw.contextRoute ?? '',
            pageTitle: raw.contextPageTitle ?? '',
            entityType: raw.contextEntityType,
            entityId: raw.contextEntityId,
            userAgent: raw.userAgent ?? ''
        },
        assignedToUserId: raw.assignedToUserId,
        assignedToUserName: raw.assignedToName,
        assignedByUserId: raw.assignedByUserId,
        assignedAt: raw.assignedAt,
        statusChangedAt: raw.statusChangedAt,
        statusChangedByUserId: raw.statusChangedByUserId,
        attachments: raw.attachments ?? [],
        notes: (raw.notes ?? []).map(mapNote)
    };
}

function mapPage(raw: PaginatedResponse<RawFeedbackListItem>): PaginatedResponse<FeedbackListItem> {
    return { ...raw, items: raw.items.map(mapListItem) };
}

@Injectable({
    providedIn: 'root'
})
export class FeedbackApiService {
    private readonly apiUrl = environment.urls.api;

    private readonly _httpClient = inject(HttpClient);

    /** POST /api/v1/Feedback — multipart so the optional attachment can ride along with the fields. */
    public createFeedback(payload: CreateFeedbackPayload): Observable<FeedbackDetail> {
        const formData = new FormData();
        formData.append('Category', String(payload.category));
        formData.append('Description', payload.description);
        formData.append('ContextRoute', payload.route);
        formData.append('ContextPageTitle', payload.pageTitle);
        if (payload.entityType) formData.append('ContextEntityType', payload.entityType);
        if (payload.entityId != null) formData.append('ContextEntityId', String(payload.entityId));
        if (payload.attachment) formData.append('Attachment', payload.attachment);
        // UserAgent is captured server-side from the request header, not sent by the client.

        return this._httpClient.post<RawFeedbackDetail>(`${this.apiUrl}/Feedback`, formData).pipe(map(mapDetail));
    }

    /** GET /api/v1/Feedback/mine — one page of the caller's own submissions. */
    public getMyFeedback(query: MyFeedbackListQuery): Observable<PaginatedResponse<FeedbackListItem>> {
        return this._httpClient
            .get<PaginatedResponse<RawFeedbackListItem>>(`${this.apiUrl}/Feedback/mine`, {
                params: this.buildParams({ Page: query.page, PageSize: query.pageSize, Status: query.status })
            })
            .pipe(map(mapPage));
    }

    /** GET /api/v1/Feedback/{id} — own feedback only; 403/404 for anything else. */
    public getMyFeedbackById(id: number): Observable<FeedbackDetail> {
        return this._httpClient.get<RawFeedbackDetail>(`${this.apiUrl}/Feedback/${id}`).pipe(map(mapDetail));
    }

    /** GET /api/v1/AdminFeedback — paged/filterable, Super Admin + Admin Users only. */
    public getAdminFeedbackList(query: AdminFeedbackListQuery): Observable<PaginatedResponse<FeedbackListItem>> {
        return this._httpClient
            .get<PaginatedResponse<RawFeedbackListItem>>(`${this.apiUrl}/AdminFeedback`, {
                params: this.buildParams({
                    Page: query.page,
                    PageSize: query.pageSize,
                    Status: query.status,
                    Category: query.category,
                    CreatedByRole: query.createdByRole,
                    Search: query.search,
                    // Bound to `GetFeedbackQuery.FromDate`/`ToDate` on the backend.
                    FromDate: query.dateFrom,
                    ToDate: query.dateTo
                })
            })
            .pipe(map(mapPage));
    }

    /** GET /api/v1/AdminFeedback/{id} — full detail incl. context, attachments, notes. */
    public getAdminFeedbackById(id: number): Observable<FeedbackDetail> {
        return this._httpClient.get<RawFeedbackDetail>(`${this.apiUrl}/AdminFeedback/${id}`).pipe(map(mapDetail));
    }

    /** PATCH /api/v1/AdminFeedback/{id}/status */
    public updateFeedbackStatus(id: number, payload: UpdateFeedbackStatusPayload): Observable<unknown> {
        return this._httpClient.patch<unknown>(`${this.apiUrl}/AdminFeedback/${id}/status`, payload);
    }

    /** PATCH /api/v1/AdminFeedback/{id}/assign */
    public assignFeedback(id: number, payload: AssignFeedbackPayload): Observable<unknown> {
        return this._httpClient.patch<unknown>(`${this.apiUrl}/AdminFeedback/${id}/assign`, payload);
    }

    /** POST /api/v1/AdminFeedback/{id}/notes */
    public addFeedbackNote(id: number, payload: AddFeedbackNotePayload): Observable<unknown> {
        return this._httpClient.post<unknown>(`${this.apiUrl}/AdminFeedback/${id}/notes`, payload);
    }

    /** GET /api/v1/AdminFeedback/assignable-users — Super Admin + Admin Users, for the assignee dropdown. */
    public getAssignableUsers(): Observable<AssignableUser[]> {
        return this._httpClient.get<AssignableUser[]>(`${this.apiUrl}/AdminFeedback/assignable-users`);
    }

    private buildParams(values: Record<string, number | string | null | undefined>): HttpParams {
        let params = new HttpParams();
        for (const [key, value] of Object.entries(values)) {
            if (value !== null && value !== undefined && value !== '') {
                params = params.set(key, String(value));
            }
        }
        return params;
    }
}
